<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PointCloudDemoUrlPatch, PointDrawMode } from '../utils/pointCloudDemoUrlConfig'

const props = withDefaults(
  defineProps<{
    /** 场景内所有点云资产的总点数（metadata.pointCount 之和） */
    totalPoints: number
    /** 当前视口实际激活（可见并参与绘制）的点数 */
    visiblePoints: number
    /** 当前全局点预算 */
    budget: number
    /** 当前可见节点数 */
    visibleNodeCount?: number
    /** 预算调整后的提示文案 */
    hint?: string
    /** 当前点云绘制链路 */
    drawMode?: PointDrawMode
    /** 仅 WebGPU 显示绘制链路切换 */
    showDrawMode?: boolean
    /** grouped 每个 draw call 最多点数 */
    groupedDrawPointLimit?: number
    /** 仅 WebGPU + grouped 显示 draw 预算控制 */
    showGroupedDrawPointLimit?: boolean
    /** 是否按 draw call 调色 */
    drawCallColorEnabled?: boolean
    /** 是否显示 draw call 调色按钮 */
    showDrawCallColor?: boolean
  }>(),
  {
    visibleNodeCount: 0,
    hint: '',
    drawMode: 'grouped',
    showDrawMode: false,
    groupedDrawPointLimit: 1_048_576,
    showGroupedDrawPointLimit: false,
    drawCallColorEnabled: false,
    showDrawCallColor: false,
  },
)

const emit = defineEmits<{
  (event: 'reload-config', patch: PointCloudDemoUrlPatch): void
  (event: 'toggle-draw-call-color', enabled: boolean): void
}>()

const budgetInput = ref(formatRaw(props.budget))
const groupedDrawInput = ref(formatRaw(props.groupedDrawPointLimit))

watch(
  () => props.budget,
  (value) => {
    budgetInput.value = formatRaw(value)
  },
)

watch(
  () => props.groupedDrawPointLimit,
  (value) => {
    groupedDrawInput.value = formatRaw(value)
  },
)

function formatRaw(value: number): string {
  return Number.isFinite(value) ? String(Math.max(0, Math.round(value))) : '0'
}

function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '不限'
  return Math.max(0, Math.round(value)).toLocaleString('zh-CN')
}

const totalLabel = computed(() => (props.totalPoints > 0 ? formatCount(props.totalPoints) : '尚未加载'))
const visibleLabel = computed(() => formatCount(props.visiblePoints))
const budgetLabel = computed(() => formatCount(props.budget))
const ratioLabel = computed(() => {
  if (props.totalPoints <= 0) return '—'
  return `${((props.visiblePoints / props.totalPoints) * 100).toFixed(1)}%`
})

const defaultHint = computed(
  () =>
    '点预算、绘制链路和 grouped draw 点数修改后会整页刷新，确保渲染资源按新参数重新创建。',
)

function parsePositiveInteger(value: string): number | null {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Math.max(1, Math.round(parsed))
}

function applyBudget(): void {
  const parsed = parsePositiveInteger(budgetInput.value)
  if (parsed === null) {
    budgetInput.value = formatRaw(props.budget)
    return
  }
  emit('reload-config', { pointBudget: parsed })
}

function resetBudgetInput(): void {
  budgetInput.value = formatRaw(props.budget)
}

function applyGroupedDrawPointLimit(): void {
  const parsed = parsePositiveInteger(groupedDrawInput.value)
  if (parsed === null) {
    groupedDrawInput.value = formatRaw(props.groupedDrawPointLimit)
    return
  }
  emit('reload-config', { groupedDrawPointLimit: parsed })
}

function resetGroupedDrawInput(): void {
  groupedDrawInput.value = formatRaw(props.groupedDrawPointLimit)
}

function selectDrawMode(mode: PointDrawMode): void {
  if (mode === props.drawMode) return
  emit('reload-config', { pointDrawMode: mode })
}

function toggleDrawCallColor(): void {
  emit('toggle-draw-call-color', !props.drawCallColorEnabled)
}
</script>

<template>
  <details class="panel-section" open>
    <summary class="panel-section-summary">
      <span class="summary-main">
        <span class="panel-section-title">点数与预算</span>
        <span class="panel-section-subtitle">总点数、当前激活点数与渲染预算</span>
      </span>
      <span class="summary-side">
        <span class="summary-count">{{ ratioLabel }}</span>
        <span class="summary-chevron" aria-hidden="true">›</span>
      </span>
    </summary>

    <div class="panel-section-body">
      <div class="status-grid">
        <div class="status-item">
          <span class="status-label">总点数</span>
          <strong class="status-value">{{ totalLabel }}</strong>
        </div>
        <div class="status-item">
          <span class="status-label">已激活点数</span>
          <strong class="status-value">{{ visibleLabel }}</strong>
        </div>
        <div class="status-item">
          <span class="status-label">点预算</span>
          <strong class="status-value">{{ budgetLabel }}</strong>
        </div>
        <div class="status-item">
          <span class="status-label">可见节点</span>
          <strong class="status-value">{{ visibleNodeCount }}</strong>
        </div>
      </div>

      <div v-if="showDrawMode" class="budget-field">
        <span>点云绘制链路</span>
        <div class="button-grid">
          <button
            class="panel-button"
            :class="{ active: drawMode === 'node' }"
            type="button"
            @click="selectDrawMode('node')"
          >
            逐节点
          </button>
          <button
            class="panel-button"
            :class="{ active: drawMode === 'grouped' }"
            type="button"
            @click="selectDrawMode('grouped')"
          >
            Grouped
          </button>
        </div>
      </div>

      <label v-if="showGroupedDrawPointLimit" class="budget-field">
        <span>每个 grouped draw call 最多点数</span>
        <input
          v-model="groupedDrawInput"
          class="panel-input"
          type="number"
          min="128"
          step="128"
          inputmode="numeric"
          aria-label="grouped 每个 draw call 最多点数"
          @keyup.enter="applyGroupedDrawPointLimit"
        />
      </label>

      <div v-if="showGroupedDrawPointLimit" class="button-grid">
        <button class="panel-button" type="button" @click="applyGroupedDrawPointLimit">
          应用并刷新
        </button>
        <button class="panel-button panel-button-subtle" type="button" @click="resetGroupedDrawInput">
          还原
        </button>
      </div>

      <button
        v-if="showDrawCallColor"
        class="panel-button"
        :class="{ active: drawCallColorEnabled }"
        type="button"
        @click="toggleDrawCallColor"
      >
        {{ drawCallColorEnabled ? '关闭 draw call 调色' : '按 draw call 调色' }}
      </button>

      <label class="budget-field">
        <span>重新设置点预算（应用后刷新）</span>
        <input
          v-model="budgetInput"
          class="panel-input"
          type="number"
          min="0"
          step="100000"
          inputmode="numeric"
          aria-label="点预算"
          @keyup.enter="applyBudget"
        />
      </label>

      <div class="button-grid">
        <button class="panel-button" type="button" @click="applyBudget">应用预算并刷新</button>
        <button class="panel-button panel-button-subtle" type="button" @click="resetBudgetInput">
          还原
        </button>
      </div>

      <p class="panel-hint">{{ hint || defaultHint }}</p>
    </div>
  </details>
</template>

<style scoped>
.budget-field {
  display: grid;
  gap: 6px;
  color: #92aebf;
  font-size: 12px;
}
</style>
